// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Radio, { RadioPresentationProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import updateDraftParameter from "@store/Framework/Store/Greif/Pages/TrailerTrackingV2/Handlers/UpdateDraftParameter";
import {
    useTrailerTrackingV2DebouncedUpdateDraftParameter,
    useTrailerTrackingV2State,
} from "@store/Framework/Store/Greif/Pages/TrailerTrackingV2/TrailerTrackingV2Hooks";
import { TrailerTrackingV2DwellTimeOperatorApiValues } from "@store/Framework/Types/ApiModels";
import FilterDrawerSection from "@store/Library/Components/FilterDrawerSection";
import { filterDrawerProps } from "@store/Themes/common/props/filterDrawer";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { visuallyHiddenStyles } from "@toolkit/Themes/common/mixins/common";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

const dwellTimeOperatorEntries = Object.entries(TrailerTrackingV2DwellTimeOperatorApiValues);
const defaultDwellTimeOperator = "all";

export interface TrailerTrackingV2FilterFieldDwellTimeProps {}

export interface TrailerTrackingV2FilterFieldDwellTimeStyles {
    daysTextField?: TextFieldPresentationProps;
    fieldWrapper?: InjectableCss;
    operatorRadio?: RadioPresentationProps;
    operatorRadioGroup?: RadioGroupProps;
}

export const trailerTrackingV2FilterFieldDwellTimeStyles: TrailerTrackingV2FilterFieldDwellTimeStyles = {
    fieldWrapper: filterDrawerProps.fieldWrapper,
    operatorRadioGroup: {
        labelProps: {
            css: visuallyHiddenStyles(),
        },
    },
    operatorRadio: filterDrawerProps.radio,
    daysTextField: mergeProps<TrailerTrackingV2FilterFieldDwellTimeStyles["daysTextField"]>(
        filterDrawerProps.textField,
        {
            mergeCss: true,
            cssOverrides: {
                formInputWrapper: css`
                    max-width: ${pxToRem(192)};
                `,
            },
            labelProps: filterDrawerProps.subLabel,
        },
    ),
};

const styles = trailerTrackingV2FilterFieldDwellTimeStyles;

const TrailerTrackingV2FilterFieldDwellTime: FC<TrailerTrackingV2FilterFieldDwellTimeProps> = () => {
    const dispatch = useAppDispatch();
    const { dwellTimeDays, dwellTimeOperator } = useTrailerTrackingV2State().draftParameter;
    const debouncedUpdateDraftParameter = useTrailerTrackingV2DebouncedUpdateDraftParameter();
    const [days, setDays] = useState(dwellTimeDays);

    useEffect(() => {
        if (days !== dwellTimeDays) {
            setDays(dwellTimeDays);
        }
    }, [dwellTimeDays]);

    return (
        <FilterDrawerSection title={translate("Dwell Time")} isAccordion={true}>
            <StyledWrapper {...styles.fieldWrapper}>
                <RadioGroup
                    {...styles.operatorRadioGroup}
                    value={dwellTimeOperator || defaultDwellTimeOperator}
                    onChangeHandler={event => {
                        const value = event.currentTarget.value;
                        dispatch(
                            updateDraftParameter({
                                dwellTimeOperator: value === defaultDwellTimeOperator ? "" : value,
                            }),
                        );
                    }}
                    label={translate("Dwell Time Operator")}
                >
                    <Radio {...styles.operatorRadio} value={defaultDwellTimeOperator}>
                        {translate("All")}
                    </Radio>
                    {dwellTimeOperatorEntries.map(([key, value]) => {
                        return (
                            <Radio {...styles.operatorRadio} key={key} value={value}>
                                {translate(`TrailerTrackingDwellTimeOperator_${key}`)}
                            </Radio>
                        );
                    })}
                </RadioGroup>
            </StyledWrapper>
            <StyledWrapper {...styles.fieldWrapper}>
                <TextField
                    {...styles.daysTextField}
                    disabled={!dwellTimeOperator}
                    error={dwellTimeOperator && !days && siteMessage("Field_Required", translate("Days"))}
                    label={
                        <>
                            <VisuallyHidden>{translate("Dwell Time")}</VisuallyHidden>
                            {translate("Days")}
                        </>
                    }
                    min={0}
                    onChange={event => {
                        const value = event.currentTarget.value;
                        const parsedValue = value ? parseInt(value, 10) : undefined;

                        setDays(parsedValue);
                        debouncedUpdateDraftParameter({ dwellTimeDays: parsedValue });
                    }}
                    placeholder={translate("Enter Days")}
                    step={1}
                    type="number"
                    value={days || ""}
                />
            </StyledWrapper>
        </FilterDrawerSection>
    );
};

export default TrailerTrackingV2FilterFieldDwellTime;
